import { Component, ElementRef, ViewEncapsulation } from '@angular/core';
import { line, Path, path } from 'd3';
import { SelectionBarChartComponent } from '../selection-bar-chart/selection-bar-chart.component';

// tslint:disable:restrict-plus-operands -- too many violations using w3c and other stuff

@Component({
  providers: [],
  selector: 'muni-gameday-combine-bar-chart',
  templateUrl: './combine-bar-chart.template.html',
  styleUrls: ['./combine-bar-chart.styles.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CombineBarChartComponent extends SelectionBarChartComponent {
  constructor(element: ElementRef) {
    super(element);
  }

  protected handleSelection() {
    this._drawSelectionBackground();
    this._drawSplitCombineButton();
    this._drawCloseButton();
    this._drawCombinedUnderline();
    this._drawSelectionUnderline();
    this._transformExistingElements();
  }

  protected _drawCloseButton() {
    const buttonRadius = 13,
      y = (buttonRadius * 2) / 3,
      x = (d) =>
        this.x(d.x) +
        this.x().bandwidth() +
        this.extraElementCfg.extendBarWidthOn * d.barsCount -
        buttonRadius / 2,
      handler = (d) => this.emitEvent('cross-click', undefined),
      sign = (d) => this.icons.times;

    this._drawButton(
      'cross',
      x,
      y,
      buttonRadius,
      (d) => d.expanded,
      sign,
      handler
    );
  }

  protected _drawSplitCombineButton() {
    const buttonRadius = 13,
      y = (buttonRadius * 2) / 3,
      x = (d) => this.x(d.x) + buttonRadius / 2,
      showRule = (d) => d.expanded && (d.combined || d.barsCount > 1),
      handler = (d) =>
        this.emitEvent('combine-click', super.getCombined(d.x.value)),
      sign = (d) => (d.combined ? this.icons.unlink : this.icons.link);

    this._drawButton(
      'combine-split',
      x,
      y,
      buttonRadius,
      showRule,
      sign,
      handler
    );
  }

  protected _drawButton(
    name: string,
    x: any,
    y: any,
    radius: number,
    showRule: any,
    sign: any,
    handler: any
  ) {
    const buttonCircle = this.chart
      .selectAll('.bar-group')
      .selectAll(`.${name}-circle`)
      .data((d) => (showRule(d) ? [d] : []));
    buttonCircle
      .enter()
      .append('circle')
      .attr('class', `${name}-circle border`)
      .attr('cx', x)
      .attr('cy', y)
      .attr('r', radius)
      .attr('filter', 'url(#dropshadow)')
      .on('click', handler)
      .transition()
      .duration(this.cfg.duration)
      .attr('y', y);

    buttonCircle
      .transition()
      .duration(this.cfg.duration)
      .attr('cx', x)
      .attr('cy', y);

    buttonCircle.exit().remove();

    const signTopOffset = 1;
    const buttonSign = this.chart
      .selectAll('.bar-group')
      .selectAll(`.${name}-sign`)
      .data((d) => (showRule(d) ? [d] : []));
    buttonSign
      .enter()
      .append('text')
      .attr('class', `${name}-sign symbol`)
      .attr('x', x)
      .attr('y', y)
      .text(sign)
      .on('click', handler)
      .transition()
      .duration(this.cfg.duration)
      .attr('y', (d) => y + radius / 2 - signTopOffset);

    buttonSign
      .transition()
      .duration(this.cfg.duration)
      .attr('x', x)
      .attr('y', (d) => y + radius / 2 - signTopOffset)
      .text(sign);

    buttonSign.exit().remove();
  }

  protected bezierLine() {
    return line()
      .x((d) => d[0])
      .y((d) => d[1])
      .curve();
  }
  protected _drawCombinedUnderline() {
    const height = 10,
      yValue = this.cfg.svg.actualHeight + this.cfg.margin.bottom - 13,
      width = this.x().bandwidth();
    const combineCurvePoints = (d: any, y: number): Path => {
      const x = this.x(d.parts[d.parts.length - 1].x);
      const rect = path();

      rect.moveTo(x, y);
      rect.bezierCurveTo(
        x,
        y + height * 2,
        x + width,
        y + height * 2,
        x + width,
        y
      );
      return rect;
    };

    const underline = this.chart
      .selectAll('.selection-layer')
      .selectAll('.combined-underline')
      .data((d: any) => (!d.expanded && d.combined ? [d.bars[0]] : []));

    underline
      .enter()
      .append('path')
      .attr('class', 'combined-underline')
      .attr(
        'd',
        (d) =>
          this.bezierLine().bind(this, combineCurvePoints(d, yValue + 10))()
            ._context._
      )
      .transition()
      .duration(this.cfg.duration)
      .attr(
        'd',
        (d) =>
          this.bezierLine().bind(this, combineCurvePoints(d, yValue))()._context
            ._
      );

    underline
      .transition()
      .duration(this.cfg.duration)
      .attr(
        'd',
        (d) =>
          this.bezierLine().bind(this, combineCurvePoints(d, yValue))()._context
            ._
      );

    underline.exit().remove();
  }
}
